import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { FiVolume2 } from "react-icons/fi";
import { GoAlertFill } from "react-icons/go";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMicrophoneController } from "@/hooks/use-audio-devices";
import { cx } from "@/lib/tailwind-utils";
import { MicPermissionWarning } from "./mic-permission-warning";

// store hooks not needed; controller hook abstracts them

interface MicrophoneInputLevelProps {
  stream: MediaStream | null;
  selectedDevice: string | undefined;
  resetMaxLevel?: boolean;
}
const MicrophoneInputLevel: React.FC<MicrophoneInputLevelProps> = t0 => {
  const $ = _c(41);
  const {
    stream,
    selectedDevice,
    resetMaxLevel: t1
  } = t0;
  const resetMaxLevel = t1 === undefined ? false : t1;
  const [inputLevel, setInputLevel] = useState(0);
  const [maxInputLevel, setMaxInputLevel] = useState(0);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const previousLevelsRef = useRef(t2);
  let t3;
  let t4;
  if ($[1] !== resetMaxLevel) {
    t3 = () => {
      if (resetMaxLevel) {
        setMaxInputLevel(0);
      }
    };
    t4 = [resetMaxLevel];
    $[1] = resetMaxLevel;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = newValue => {
      const levels = previousLevelsRef.current;
      levels.push(newValue);
      if (levels.length > 3) {
        levels.shift();
      }
      let totalWeight = 0;
      let weightedSum = 0;
      for (let i = 0; i < levels.length; i++) {
        const weight = i + 1;
        weightedSum = weightedSum + levels[i] * weight;
        totalWeight = totalWeight + weight;
      }
      return weightedSum / totalWeight;
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const smoothValue = t5;
  let t6;
  let t7;
  if ($[5] !== maxInputLevel || $[6] !== stream) {
    t6 = () => {
      if (!stream) {
        return;
      }
      let audioContext = null;
      ;
      try {
        audioContext = new AudioContext();
        const audioSource = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        audioSource.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;
        const updateLevel = () => {
          if (!analyserRef.current || !dataArrayRef.current) {
            return;
          }
          analyserRef.current.getByteTimeDomainData(dataArrayRef.current);
          let sumOfSquares = 0;
          for (let i_0 = 0; i_0 < dataArrayRef.current.length; i_0++) {
            const amplitude = dataArrayRef.current[i_0] - 128;
            sumOfSquares = sumOfSquares + amplitude * amplitude;
          }
          const rms = Math.sqrt(sumOfSquares / dataArrayRef.current.length);
          const amplifiedLevel = Math.min(100, rms / 128 * 200);
          const smoothedLevel = smoothValue(amplifiedLevel);
          setInputLevel(smoothedLevel);
          if (smoothedLevel > maxInputLevel) {
            setMaxInputLevel(smoothedLevel);
          }
          animationRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();
      } catch (t8) {
        const err = t8;
        console.error("Error setting up audio analysis:", err);
      }
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (audioContext) {
          audioContext.close().catch(_temp);
        }
        previousLevelsRef.current = [];
      };
    };
    t7 = [stream, maxInputLevel];
    $[5] = maxInputLevel;
    $[6] = stream;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMaxInputLevel(0);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== selectedDevice) {
    t9 = [selectedDevice];
    $[10] = selectedDevice;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  useEffect(t8, t9);
  const filledBars = Math.round(inputLevel / 100 * 16);
  const maxVolumeBar = Math.round(maxInputLevel / 100 * 16);
  const lowThreshold = Math.max(2, Math.floor(maxInputLevel * 0.25 / 100 * 16));
  const highThreshold = Math.max(14, Math.floor(maxInputLevel * 0.85 / 100 * 16));
  let feedbackMessage;
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[12] !== filledBars || $[13] !== highThreshold || $[14] !== lowThreshold || $[15] !== maxVolumeBar) {
    let barColor = "";
    let lightBarColor = "";
    if (maxVolumeBar <= lowThreshold) {
      feedbackMessage = "Low volume";
      barColor = "bg-yellow-400";
      lightBarColor = "bg-yellow-400/20";
    } else {
      if (maxVolumeBar >= highThreshold) {
        feedbackMessage = "Volume too loud";
        barColor = "bg-red-500";
        lightBarColor = "bg-red-500/10";
      } else {
        feedbackMessage = "Good volume input";
        barColor = "bg-primary/70";
        lightBarColor = "bg-primary/10";
      }
    }
    t12 = "space-y-1";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="flex items-center gap-2"><FiVolume2 className="h-4 w-4 text-foreground" /><p className="font-medium">Input level:</p></div>;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    t10 = "flex h-2.5 w-full gap-1";
    t11 = Array.from({
      length: 16
    }).map((_, index) => <div key={index} className={cx("flex-1 rounded-full", index < filledBars ? barColor : lightBarColor)} />);
    $[12] = filledBars;
    $[13] = highThreshold;
    $[14] = lowThreshold;
    $[15] = maxVolumeBar;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = feedbackMessage;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    feedbackMessage = $[20];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t11) {
    t14 = <div className={t10}>{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = maxVolumeBar <= lowThreshold ? "font-semibold text-yellow-600" : maxVolumeBar >= highThreshold ? "font-semibold text-red-600" : "text-primary";
  let t16;
  if ($[25] !== t15) {
    t16 = cx("text-caption", t15);
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== lowThreshold || $[28] !== maxVolumeBar) {
    t17 = maxVolumeBar <= lowThreshold && <GoAlertFill className="mr-1 inline-block h-3 w-3" />;
    $[27] = lowThreshold;
    $[28] = maxVolumeBar;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== feedbackMessage) {
    t18 = <span>{feedbackMessage}</span>;
    $[30] = feedbackMessage;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t16 || $[33] !== t17 || $[34] !== t18) {
    t19 = <p className={t16}>{t17}{t18}</p>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t14 || $[39] !== t19) {
    t20 = <div className={t12}>{t13}{t14}{t19}</div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
};
const MicrophoneSelect: React.FC = () => {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      enablePreview: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    audioDevices,
    selectedDevice,
    previewStream,
    inputLevelResetToken,
    requestMicPermission,
    selectDevice
  } = useMicrophoneController(t0);
  let t1;
  if ($[1] !== selectDevice) {
    t1 = deviceId => {
      selectDevice(deviceId);
    };
    $[1] = selectDevice;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleDeviceChange = t1;
  let t2;
  let t3;
  if ($[3] !== requestMicPermission) {
    t2 = () => {
      requestMicPermission();
    };
    t3 = [requestMicPermission];
    $[3] = requestMicPermission;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== audioDevices || $[7] !== handleDeviceChange || $[8] !== inputLevelResetToken || $[9] !== previewStream || $[10] !== selectedDevice) {
    t4 = audioDevices.length > 0 && <><Label htmlFor="microphone">Select Microphone</Label><Select value={selectedDevice} onValueChange={handleDeviceChange}><SelectTrigger className="mb-2 w-full max-w-md"><SelectValue placeholder="Select a microphone" /></SelectTrigger><SelectContent>{audioDevices.filter(_temp2).map(_temp3)}</SelectContent></Select>{previewStream && <MicrophoneInputLevel stream={previewStream} selectedDevice={selectedDevice} resetMaxLevel={inputLevelResetToken} />}</>;
    $[6] = audioDevices;
    $[7] = handleDeviceChange;
    $[8] = inputLevelResetToken;
    $[9] = previewStream;
    $[10] = selectedDevice;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MicPermissionWarning />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4) {
    t6 = <div>{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export { MicrophoneInputLevel, MicrophoneSelect };
export default MicrophoneSelect;
function _temp(err_0) {
  return console.error("Error closing audio context:", err_0);
}
function _temp2(device) {
  return device.deviceId !== "";
}
function _temp3(device_0) {
  return <SelectItem key={device_0.deviceId} value={device_0.deviceId}>{device_0.label}</SelectItem>;
}